import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Avatar,
  IconButton,
  CircularProgress,
  Alert,
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAppContext } from '../context/AppContext';
import { UserData } from '../types';

const Profile: React.FC = () => {
  const navigate = useNavigate();
  const { user, userData, setUserData } = useAppContext();
  const [name, setName] = useState('');
  const [bio, setBio] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (userData) {
      setName(userData.name || '');
      setBio(userData.bio || '');
    }
  }, [userData]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    setLoading(true);
    setError('');

    try {
      const docRef = doc(db, 'users', user.uid);
      await updateDoc(docRef, { name, bio } as Partial<UserData>);
      const snap = await getDoc(docRef);
      setUserData(snap.data() as UserData);
      navigate('/chat');
    } catch {
      setError('Failed to update profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const inputSx = {
    '& .MuiOutlinedInput-root': {
      bgcolor: 'rgba(255,255,255,0.03)',
      '& fieldset': { borderColor: 'rgba(255,255,255,0.08)' },
      '&:hover fieldset': { borderColor: 'rgba(124,77,255,0.4)' },
      '&.Mui-focused fieldset': { borderColor: '#7c4dff' },
    },
    '& .MuiInputLabel-root': { color: '#8888a8' },
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background:
          'radial-gradient(ellipse at 30% 40%, rgba(124,77,255,0.12) 0%, transparent 50%), ' +
          'radial-gradient(ellipse at 70% 60%, rgba(255,64,129,0.08) 0%, transparent 50%), ' +
          '#0a0a14',
        p: 2,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          p: 5,
          maxWidth: 500,
          width: '100%',
          borderRadius: 4,
          background: 'rgba(18,18,31,0.8)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255,255,255,0.06)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <IconButton
            onClick={() => navigate('/chat')}
            sx={{ mr: 1, color: '#8888a8' }}
          >
            <ArrowBack />
          </IconButton>
          <Typography variant="h5" sx={{ color: '#e8e8f0' }}>
            Profile Details
          </Typography>
        </Box>

        {error && (
          <Alert
            severity="error"
            sx={{
              mb: 2,
              bgcolor: 'rgba(255,64,64,0.08)',
              border: '1px solid rgba(255,64,64,0.15)',
            }}
          >
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit}>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
            <Avatar
              src={userData?.avatar}
              sx={{
                width: 120,
                height: 120,
                fontSize: 48,
                background: 'linear-gradient(135deg, #7c4dff, #ff4081)',
                boxShadow: '0 0 30px rgba(124,77,255,0.3)',
              }}
            >
              {name?.[0]?.toUpperCase()}
            </Avatar>
          </Box>

          <TextField
            fullWidth
            label="Display Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            sx={{ mb: 2, ...inputSx }}
          />
          <TextField
            fullWidth
            label="Bio"
            value={bio}
            onChange={(e) => setBio(e.target.value)}
            multiline
            rows={3}
            sx={{ mb: 3, ...inputSx }}
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            disabled={loading}
            sx={{
              py: 1.5,
              background: 'linear-gradient(135deg, #7c4dff, #ff4081)',
              boxShadow: '0 4px 20px rgba(124,77,255,0.3)',
              '&:hover': {
                background: 'linear-gradient(135deg, #6a3de8, #e8356f)',
              },
            }}
          >
            {loading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              'Save Profile'
            )}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default Profile;
